import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  NgDialogModule,
  TypedFormControl,
  TypedFormGroup,
} from '@principle-theorem/ng-shared';
import { StaffSelectorComponent } from '@principle-theorem/ng-principle-shared';
import { stafferToNamedDoc } from '@principle-theorem/principle-core';
import { PatientPermissions } from '@principle-theorem/principle-core/features';
import {
  IAttributableLineItem,
  isFeeLineItem,
  isProductLineItem,
  type IPractice,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import { type INamedDocument, type WithRef } from '@principle-theorem/shared';

export interface IPractitionerAttributionDialogRequest {
  practice?: WithRef<IPractice>;
  lineItem?: IAttributableLineItem;
}

export interface IPractitionerAttributionDialogResponse {
  attributedTo?: INamedDocument<IStaffer>;
}

interface IPractitionerAttributionFormData {
  staffer: INamedDocument<IStaffer>;
}

@Component({
  selector: 'pr-practitioner-attribution-dialog',
  templateUrl: './practitioner-attribution-dialog.component.html',
  styleUrls: ['./practitioner-attribution-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgDialogModule, StaffSelectorComponent],
})
export class PractitionerAttributionDialogComponent {
  permissions = [PatientPermissions.ReceiveDeposits];
  isEdit: boolean = false;
  dialogTitle: string = 'Allocate to practitioner';
  attributionForm = new TypedFormGroup<IPractitionerAttributionFormData>({
    staffer: new TypedFormControl<INamedDocument<IStaffer> | undefined>(),
  });

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: IPractitionerAttributionDialogRequest,
    public dialogRef: MatDialogRef<
      PractitionerAttributionDialogComponent,
      IPractitionerAttributionDialogResponse
    >
  ) {
    if (data.lineItem) {
      this.isEdit = true;
      this.dialogTitle = this.getDialogTitle(data.lineItem);
      this.attributionForm.patchValue({
        staffer: data.lineItem.attributedTo,
      });
    }
  }

  submit(): void {
    if (!this.attributionForm.valid) {
      return;
    }
    const formValue = this.attributionForm.value;
    const attributedTo = formValue.staffer
      ? stafferToNamedDoc(formValue.staffer)
      : undefined;
    this.dialogRef.close({
      attributedTo,
    });
  }

  private getDialogTitle(lineItem: IAttributableLineItem): string {
    if (isProductLineItem(lineItem)) {
      return 'Allocate product to practitioner';
    }
    if (isFeeLineItem(lineItem)) {
      return 'Allocate fee to practitioner';
    }
    return 'Allocate to practitioner';
  }
}
